/**
 * Ermittelt BIC und Bankname aus einer IBAN
 * Unterstützt hauptsächlich deutsche IBANs, kann aber erweitert werden
 * 
 * WICHTIG: Diese Funktion verwendet jetzt die Datenbank statt statischer Daten.
 * Die Funktion ist asynchron und benötigt einen API-Client.
 */

export interface BankData {
  bic?: string;
  bankName?: string;
}

/**
 * API-Client-Typ für Bankdaten-Abfragen
 */
export interface BankDataApiClient {
  get<T>(url: string): Promise<T>;
}

/**
 * Extrahiert die BLZ (Bankleitzahl) aus einer deutschen IBAN
 * Deutsche IBAN: DE + 2 Prüfziffern + 8-stellige BLZ + 10-stellige Kontonummer
 */
function extractBLZFromGermanIBAN(iban: string): string | null {
  const normalized = iban.replace(/\s/g, "").toUpperCase();
  if (!normalized.startsWith("DE") || normalized.length !== 22) {
    return null;
  }
  // BLZ ist Position 4-11 (nach DE + 2 Prüfziffern)
  return normalized.substring(4, 12);
}

/**
 * Ermittelt BIC und Bankname aus einer IBAN (asynchron, aus Datenbank)
 * @param iban - Die IBAN (kann mit Leerzeichen eingegeben werden)
 * @param api - API-Client für Datenbankabfragen
 * @returns Bankdaten mit BIC und Bankname, oder null wenn nicht gefunden
 */
export async function getBankDataFromIban(iban: string, api: BankDataApiClient): Promise<BankData | null> {
  if (!iban) return null;
  
  const normalized = iban.replace(/\s/g, "").toUpperCase().trim();
  if (normalized.length < 15) return null;
  
  const countryCode = normalized.substring(0, 2);
  
  // Für deutsche IBANs: BLZ extrahieren und in Datenbank nachschlagen
  if (countryCode === "DE" && normalized.length === 22) {
    const blz = extractBLZFromGermanIBAN(normalized);
    if (blz) {
      try {
        const data = await api.get<{ bankleitzahl: string; bic: string; bankName: string; shortName?: string }>(`/api/v1/bank-data/by-blz/${blz}`);
        if (data) {
          return {
            bic: data.bic,
            bankName: data.bankName
          };
        }
      } catch (err) {
        // Bank nicht gefunden oder API-Fehler - ignoriere still
        console.debug("Bank data not found for bank code:", blz, err);
      }
    }
  }
  
  // Für andere Länder: Aktuell keine automatische Ermittlung
  return null;
}
